import React, { useState, useEffect, useContext, useRef, memo } from 'react'
import SearchIcon from '@material-ui/icons/Search'
import KeyboardArrowLeftIcon from '@material-ui/icons/KeyboardArrowLeft'
import KeyboardArrowRightIcon from '@material-ui/icons/KeyboardArrowRight'
import FirstPageIcon from '@material-ui/icons/FirstPage'
import LastPageIcon from '@material-ui/icons/LastPage'
import ArrowDropUpIcon from '@material-ui/icons/ArrowDropUp'
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown'
import MoreHorizIcon from '@material-ui/icons/MoreHoriz'
import PersonOutlineIcon from '@material-ui/icons/PersonOutline'
import PersonAddAltIcon from '@material-ui/icons/PersonAddAlt'
import ControlPointDuplicateIcon from '@material-ui/icons/ControlPointDuplicate'
import SmsOutlinedIcon from '@material-ui/icons/SmsOutlined'
import CloseIcon from '@material-ui/icons/Close'
import {
    IconButton,
    InputAdornment,
    Link,
    makeStyles,
    TextField,
    TextFieldProps,
    Typography,
    useTheme,
} from '@material-ui/core'
import useMuseStyles from '../../themes/useMuseStyles'
import useDebounce from '../../hooks/useDebounce'
import { useCommunityFeature } from '../../services/useCommunityFeature'
import {
    CommunityPageTabTypes,
    UserCommunityProfileView,
    UserContext,
    UserContextType,
    UserProfilesSearchResult,
} from '../../UserContext'
import CommunityTableActionButton from './CommunityTableActionButton'
import ConfirmationDialogContents from '../ConfirmationDialogContents'
import { AppContext, AppContextType } from '../../AppContext'
import clsx from 'clsx'
import CommunityProfileView from './CommunityProfileView'
import { AbstractInviteToSessionInteractable } from './CommunityMessagesChatPage'
import { Trans, useTranslation } from 'react-i18next'
import { RoomContext, RoomContextType } from '../../RoomContext'
import CrossPlatformDialog from '@/dialogs/components/CrossPlatformDialog'

type SortObject = {
    username?: number
    activeStatus?: number
}

const useMuseInputStyle = makeStyles((theme) => ({
    inputText: {
        fontFamily: 'Inv Maison Neue,-apple-system,BlinkMacSystemFont,Open Sans,sans-serif',
        fontSize: '14px',
        lineHeight: '14px',
        letterSpacing: 0,
        color: '#5D5D5D',

        '&.MuiInputBase-input': {
            color: '#aaa',
        },
        [theme.breakpoints.down('xs')]: {
            fontSize: '16px',
            lineHeight: '16px',
        },
    },

    inputTextFilled: {
        '&.MuiInputBase-input': {
            color: '#F9CC04',
        },
    },

    textArea: {
        width: '100%',
        display: 'inline-flex',

        '& .MuiInput-underline:before': {
            borderBottom: '1px solid #5D5D5D',
            transition: 'none',
        },
        '& .MuiInput-underline:after': {
            transition: 'none',
            borderBottom: '1px solid #F9CC04',
        },
        '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
            borderBottom: '1px solid #F9CC04',
            transition: 'none',
        },
        '& .MuiInput-underline.Mui-error:after': {
            borderBottom: '1px solid #f44336',
        },
        '& .MuiFormLabel-root': {
            color: '#5D5D5D',
        },
        // Styles for variant=outlined
        '& .MuiOutlinedInput-root': {
            border: 'solid 1px #444444',
            backgroundColor: '#282828',
            borderRadius: 5,
            color: '#f1f1f1',
        },
        '& .MuiOutlinedInput-root > .MuiInputBase-input::placeholder': {
            color: '#ffffff',
        },
        '& .MuiOutlinedInput-root:focus-within': {
            border: 'solid 1px #F9CC04',
            borderRadius: 5,
        },
        '& .MuiOutlinedInput-multiline': {
            padding: '4px 14px',
        },
        '& .MuiOutlinedInput-root > .MuiInputBase-input': {
            paddingTop: 10,
            paddingBottom: 10,
            lineHeight: '140%',
            color: '#f1f1f1',
        },
        '& .MuiOutlinedInput-root > .MuiInputBase-input:focus': {
            outline: 'unset',
        },
        '& .MuiInputAdornment-root': {
            color: '#F9CC04',
        },
        '& .MuiButtonBase-root': {
            color: '#F9CC04',
        },
        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
            // Not setting this causes a huge border to appear on input focus
            borderWidth: 0,
        },
        '& .Mui-disabled': {
            color: '#f1f1f1d0 !important',
        },
    },

    textAreaFilled: {
        '& .MuiInput-underline:before': {
            borderBottom: '1px solid #F9CC04',
        },
        '& .MuiAutocomplete-inputRoot .MuiAutocomplete-input': {
            color: '#F9CC04',
        },
    },
}))

export const MuseInput = memo(function MuseInput(props: TextFieldProps) {
    const classes = useMuseInputStyle(useTheme())

    const className = props.value && !props.disabled ? clsx(classes.textArea, classes.textAreaFilled) : classes.textArea

    const inputTextClassName =
        props.value && !props.disabled ? clsx(classes.inputText, classes.inputTextFilled) : classes.inputText

    let inputProps = { className: inputTextClassName }
    if (props.inputProps) {
        inputProps = {
            ...inputProps,
            ...props.inputProps,
        }
    }

    return <TextField {...props} className={className} inputProps={inputProps} style={props.style} />
})

function CommunityCollaboratePage({ containerRef }: { containerRef: React.MutableRefObject<HTMLDivElement | null> }) {
    const gclasses = useMuseStyles(useTheme())

    const { t } = useTranslation()

    const { state, dispatch } = useContext(UserContext) as UserContextType

    const communityFeature = useCommunityFeature()

    const [searchText, set_searchText] = useState<string>('')
    const searchText_debounced = useDebounce(searchText, 250)

    const [selectedPeerUserProfile, set_selectedPeerUserProfile] = useState<string | null>(null)

    const [isFirstLoading, set_isFirstLoading] = useState(true)
    const [currentPage, set_currentPage] = useState<number>(0)
    const [isLoadingPage, set_isLoadingPage] = useState(false)
    const [sortObject, set_sortObject] = useState<SortObject>({ activeStatus: 1 })

    const search = () => {
        const searchText_debounced_trimmed = searchText_debounced.trim()
        if (searchText_debounced_trimmed) {
            communityFeature.searchUserProfiles({
                generalQuery: searchText_debounced_trimmed,
                page: 0,
                sort: sortObject,
            })
        } else {
            communityFeature.searchUserProfiles({ all: true, page: 0, sort: sortObject })
        }
        // results in `state.community.userProfilesSearchResult` updating.
    }

    const paginate = (page: number) => {
        const searchText_debounced_trimmed = searchText_debounced.trim()
        if (searchText_debounced_trimmed) {
            communityFeature.searchUserProfiles({
                generalQuery: searchText_debounced_trimmed,
                page: page,
                sort: sortObject,
            })
        } else {
            communityFeature.searchUserProfiles({ all: true, page: page, sort: sortObject })
        }
        // results in `state.community.userProfilesSearchResult` updating.
    }

    useEffect(() => {
        if (containerRef.current) {
            containerRef.current.scrollTo(0, 0)
            set_isLoadingPage(false)
            set_currentPage(state.community.userProfilesSearchResult.page)
        }
        if (state.community.userProfilesSearchResult.size > 0) {
            set_isFirstLoading(false)
        }
    }, [state.community.userProfilesSearchResult])

    useEffect(() => {
        search()
    }, [searchText_debounced, sortObject])

    if (!state.communityProfile) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <Typography variant="body1" className={clsx(gclasses.textSmaller85)}>
                    <Trans
                        i18nKey={
                            'In order to find other collaborators on Muse, you need to <1>create a profile</1> first.'
                        }
                    >
                        In order to find other collaborators on Muse, you need to{' '}
                        <a
                            href="#"
                            className={clsx(gclasses.museLink, gclasses.customTooltipLink)}
                            style={{ display: 'inline-flex', fontSize: '100%' }}
                            onClick={(e) => {
                                e.preventDefault()
                                // history.push(`/pages/main-community-page?tab=profile`)
                                dispatch({
                                    type: 'update_community_state',
                                    value: { communityPageCurrentTab: 'profile' },
                                })
                            }}
                        >
                            create a profile
                        </a>{' '}
                        first.
                    </Trans>
                </Typography>
            </div>
        )
    }

    if (selectedPeerUserProfile) {
        const profile = state.community.userProfilesSearchResult.profiles.filter(
            (profile) => profile.userUrn === selectedPeerUserProfile
        )[0]
        if (profile) {
            return (
                <div style={{}}>
                    <div style={{ paddingLeft: 50 }}>
                        <IconButton
                            className={clsx(gclasses.textWhiteColor)}
                            onClick={() => {
                                set_selectedPeerUserProfile(null)
                            }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </div>
                    <CommunityProfileView profile={profile} headerText={``} readonly={true}></CommunityProfileView>
                </div>
            )
        }
    }

    return (
        <>
            <MuseInput
                style={{ padding: '0 18px 18px 18px', zIndex: 100 }}
                variant="outlined"
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <SearchIcon />
                        </InputAdornment>
                    ),
                    // endAdornment: (
                    //     <InputAdornment position="end">
                    //         <IconButton>
                    //             <SortIcon />
                    //         </IconButton>
                    //     </InputAdornment>
                    // )
                }}
                value={searchText}
                onChange={(e) => {
                    set_searchText(e.target.value)
                }}
            />

            <UserProfileTable
                profiles={state.community.userProfilesSearchResult}
                currentPage={currentPage}
                sortObject={sortObject}
                isFirstLoading={isFirstLoading}
                isLoadingPage={isLoadingPage}
                reload={() => {
                    search()
                }}
                onSelectPeerUserProfile={(userUrn: string | null) => set_selectedPeerUserProfile(userUrn)}
                onSortToggle={(toggleField: keyof SortObject) => {
                    let currentSortDirection = sortObject[toggleField] || -1
                    set_sortObject({ [toggleField]: currentSortDirection * -1 })
                }}
                onSetPage={(page) => {
                    paginate(page)
                    set_isLoadingPage(true)
                }}
            />
        </>
    )
}

function UserProfileTableRow({
    row,
    onSelectPeerUserProfile,
    communityFeature,
    set_isDeleteProfileConfirmationDialogOpen,
}: {
    row: UserCommunityProfileView
    onSelectPeerUserProfile: (userUrn: string) => void
    communityFeature: ReturnType<typeof useCommunityFeature>
    set_isDeleteProfileConfirmationDialogOpen: React.Dispatch<React.SetStateAction<{ userUrn: string } | null>>
}) {
    const { state } = useContext(UserContext) as UserContextType

    return (
        <tr>
            <td className="cell-username cell-leftmost">
                <Link
                    component="button"
                    color="primary"
                    onClick={() => onSelectPeerUserProfile(row.userUrn)}
                    style={{ fontSize: '0.75rem' }}
                >
                    {row.username}
                </Link>
            </td>
            <td className="cell-status">
                {(() => {
                    if (row.activeStatus === 'active') {
                        return <div style={{ color: '#6edf0a' }}>Available</div>
                    } else if (row.activeStatus === 'offline') {
                        return <div style={{ color: '#999' }}>Offline</div>
                    } else {
                        return <>{row.activeStatus}</>
                    }
                })()}
            </td>
            <td className="cell-genre cell-basic">{row.genre}</td>
            <td className="cell-instrument cell-basic">{row.instrument}</td>
            <td className="cell-role cell-basic">{row.role}</td>
            <td className="cell-action cell-rightmost">
                {(() => {
                    if (state.userUrn === row.userUrn) {
                        // then, is self; do not render action buttons.
                        return <div></div>
                    } else {
                        return (
                            <CollaborateTableActionRow
                                communityFeature={communityFeature}
                                userUrn={row.userUrn}
                                username={row.username}
                                collaboratorStatus={row.collaboratorStatus}
                                unfriendCollaborator={(userUrn) => {
                                    set_isDeleteProfileConfirmationDialogOpen({ userUrn })
                                }}
                            />
                        )
                    }
                })()}
            </td>
        </tr>
    )
}

function UserProfileTable({
    profiles = null,
    currentPage,
    sortObject,
    isFirstLoading,
    isLoadingPage,
    reload,
    onSelectPeerUserProfile,
    onSortToggle,
    onSetPage,
}: {
    profiles: UserProfilesSearchResult | null
    currentPage: number
    sortObject: SortObject
    isFirstLoading: boolean
    isLoadingPage: boolean
    reload: () => void
    onSelectPeerUserProfile: (userUrn: string) => void
    onSortToggle: (toggleField: keyof SortObject) => void
    onSetPage: (page: number) => void
}) {
    const gclasses = useMuseStyles(useTheme())

    const [isDeleteProfileConfirmationDialogOpen, set_isDeleteProfileConfirmationDialogOpen] = useState<{
        userUrn: string
    } | null>(null)

    const communityFeature = useCommunityFeature()

    const unfriendCollaboratorComplete_useEffect_firstCall = useRef(true)

    useEffect(() => {
        if (unfriendCollaboratorComplete_useEffect_firstCall.current) {
            unfriendCollaboratorComplete_useEffect_firstCall.current = false
        } else {
            reload()
        }
    }, [communityFeature.unfriendCollaboratorComplete])

    if (profiles == null || profiles.profiles == null) {
        return null
    }

    // @ts-ignore
    const rows = profiles.profiles.map((p) => ({
        ...p,
        name: p.username + (p.name ? `\n(${p.name})` : ''),
        genre: (p.genre || []).join(', '),
        instrument: (p.instrument || []).join(', '),
        role: (p.role || []).join(', '),
    })) as UserCommunityProfileView[]

    const pagesCt = Math.ceil(profiles.size / profiles.pageSize)
    const isFirstPage = currentPage <= 0
    const isLastPage = currentPage + 1 >= pagesCt

    if (isFirstLoading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '85%' }}>
                <Typography variant="body1" className={clsx(gclasses.textSmaller85)}>
                    Loading...
                </Typography>
            </div>
        )
    }

    if (profiles.size === 0) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '85%' }}>
                <Typography variant="body1" className={clsx(gclasses.textSmaller85)}>
                    No results
                </Typography>
            </div>
        )
    }

    function SortArrowIcon({ fieldName }: { fieldName: keyof SortObject }) {
        if (sortObject[fieldName] != null) {
            return (sortObject[fieldName] || 0) > 0 ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />
        } else {
            return null
        }
    }

    return (
        <div id="table-container" style={isLoadingPage ? { opacity: 0.5 } : {}}>
            <table className={gclasses.communityCollaboratorTable}>
                <thead>
                    <tr>
                        <th
                            className="cell-username cell-clickable cell-leftmost"
                            onClick={() => onSortToggle('username')}
                        >
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <span>USERNAME</span>
                                <SortArrowIcon fieldName="username" />
                            </div>
                        </th>
                        <th className="cell-status cell-clickable" onClick={() => onSortToggle('activeStatus')}>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <span>STATUS</span>
                                <SortArrowIcon fieldName="activeStatus" />
                            </div>
                        </th>
                        <th className="cell-genre cell-basic">GENRE</th>
                        <th className="cell-instrument cell-basic">INSTRUMENT</th>
                        <th className="cell-role cell-basic">ROLE</th>
                        <th className="cell-rightmost">ACTION</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row) => {
                        return (
                            <UserProfileTableRow
                                key={row.username}
                                row={row}
                                communityFeature={communityFeature}
                                onSelectPeerUserProfile={onSelectPeerUserProfile}
                                set_isDeleteProfileConfirmationDialogOpen={set_isDeleteProfileConfirmationDialogOpen}
                            />
                        )
                    })}
                </tbody>
            </table>

            <div className={gclasses.communityCollaboratorPaginationController}>
                <IconButton
                    className={isFirstPage || isLoadingPage ? 'disabled' : undefined}
                    disableRipple
                    onClick={() => {
                        if (!isFirstPage && !isLoadingPage) {
                            onSetPage(0)
                        }
                    }}
                >
                    <FirstPageIcon />
                </IconButton>
                <IconButton
                    className={isFirstPage || isLoadingPage ? 'disabled' : undefined}
                    disableRipple
                    onClick={() => {
                        if (!isFirstPage && !isLoadingPage) {
                            onSetPage(currentPage - 1)
                        }
                    }}
                >
                    <KeyboardArrowLeftIcon />
                </IconButton>
                <div className="page-status">
                    {currentPage + 1} / {pagesCt}
                </div>
                <IconButton
                    className={isLastPage || isLoadingPage ? 'disabled' : undefined}
                    disableRipple
                    onClick={() => {
                        if (!isLastPage && !isLoadingPage) {
                            onSetPage(currentPage + 1)
                        }
                    }}
                >
                    <KeyboardArrowRightIcon />
                </IconButton>
                <IconButton
                    className={isLastPage || isLoadingPage ? 'disabled' : undefined}
                    disableRipple
                    onClick={() => {
                        if (!isLastPage && !isLoadingPage) {
                            onSetPage(pagesCt - 1)
                        }
                    }}
                >
                    <LastPageIcon />
                </IconButton>
            </div>

            {isDeleteProfileConfirmationDialogOpen && (
                <CrossPlatformDialog
                    frameName="Dialog:UnfriendUserConfirmationDialog"
                    onDefaultClose={() => set_isDeleteProfileConfirmationDialogOpen(null)}
                >
                    <ConfirmationDialogContents
                        message={`Are you sure you want to unfriend this user?`}
                        onPositive={async () => {
                            const userUrnToUnfriend = isDeleteProfileConfirmationDialogOpen.userUrn
                            await communityFeature.unfriendCollaborator(userUrnToUnfriend)
                            set_isDeleteProfileConfirmationDialogOpen(null)
                        }}
                        onNegative={() => set_isDeleteProfileConfirmationDialogOpen(null)}
                        positiveButtonText="Yes"
                        negativeButtonText="Cancel"
                    />
                </CrossPlatformDialog>
            )}
        </div>
    )
}

function CollaborateTableActionRow({
    communityFeature,
    userUrn,
    username,
    collaboratorStatus,
    unfriendCollaborator,
}: {
    communityFeature: ReturnType<typeof useCommunityFeature>
    userUrn: string
    username: string
    collaboratorStatus: string
    unfriendCollaborator: (userUrn: string) => void
}) {
    const gclasses = useMuseStyles(useTheme())

    const { state: roomState } = useContext(RoomContext) as RoomContextType
    const { dispatch } = useContext(UserContext) as UserContextType
    const { dispatch: appDispatch } = useContext(AppContext) as AppContextType

    const setCurrentTab = (tab: CommunityPageTabTypes) => {
        dispatch({
            type: 'update_community_state',
            value: { communityPageCurrentTab: tab },
        })
    }

    return (
        <div style={{ whiteSpace: 'nowrap', display: 'flex' }}>
            {/* ---- collaborator button ---- */}
            {collaboratorStatus === 'collaborator' && (
                <CommunityTableActionButton
                    classes={{ label: gclasses.backgroundDarkColor }}
                    style={{ borderColor: '#F9CC04', backgroundColor: '#F9CC04' }}
                    title="Currently is your collaborator"
                    onClick={() => {
                        unfriendCollaborator(userUrn)
                    }}
                >
                    <PersonOutlineIcon fontSize="inherit"></PersonOutlineIcon>
                </CommunityTableActionButton>
            )}
            {collaboratorStatus === 'pending' && (
                <CommunityTableActionButton
                    onClick={() => {
                        appDispatch({
                            type: 'root_snackbar_history_append',
                            value: {
                                severity: 'info',
                                message: 'Awaiting for the user to accept your invitation!',
                            },
                        })
                    }}
                    title="Awaiting for the user to accept your invitation"
                >
                    <MoreHorizIcon fontSize="inherit"></MoreHorizIcon>
                </CommunityTableActionButton>
            )}
            {collaboratorStatus === 'not_collaborator' && (
                <CommunityTableActionButton
                    classes={{ label: gclasses.textWhiteColor }}
                    style={{ borderColor: '#f1f1f1' }}
                    onClick={() => {
                        communityFeature.sendCollaboratorInvitation(userUrn)
                    }}
                    title="Add friend"
                >
                    <PersonAddAltIcon fontSize="inherit"></PersonAddAltIcon>
                </CommunityTableActionButton>
            )}

            <div style={{ display: 'inline-block', width: 4 }}></div>

            {/* ---- message button ---- */}
            <CommunityTableActionButton
                classes={{ label: gclasses.textWhiteColor }}
                style={{ borderColor: '#f1f1f1' }}
                onClick={() => {
                    communityFeature.createNewMessageGroup([userUrn]).then(() => {
                        setCurrentTab('messages')
                    })
                }}
                title="Message this user"
            >
                <SmsOutlinedIcon fontSize="inherit"></SmsOutlinedIcon>
            </CommunityTableActionButton>

            <div style={{ display: 'inline-block', width: 4 }}></div>

            {/* ---- call button ---- */}
            {(roomState.roomUrn || roomState.preRoomUrn) && (
                <div style={{ display: 'inline-block' }}>
                    <AbstractInviteToSessionInteractable
                        id="communitycollaboratepageinvitesession-tooltip"
                        invite={{ userUrn, username }}
                        component={({ isButtonDisabled, onSendInviteClicked }) => (
                            <CommunityTableActionButton
                                classes={{ label: gclasses.textWhiteColor }}
                                style={{ borderColor: '#f1f1f1' }}
                                onClick={() => onSendInviteClicked()}
                                title="Invite to session"
                            >
                                <ControlPointDuplicateIcon fontSize="inherit"></ControlPointDuplicateIcon>
                            </CommunityTableActionButton>
                        )}
                        communityFeature={communityFeature}
                    />
                </div>
            )}
        </div>
    )
}

export default CommunityCollaboratePage
